import qs from 'qs';
import type { AddedAssetTheme } from 'fym-shared/src/music24/addedAssets';
import type {
  AddedAssetResponse,
  AddedUploadStartRequest,
  AddYouTubeRequest,
  CompleteMultipartUploadRequest,
  KitAssetStreamResponse,
  PresignedMultipartUploadResponse,
  YouTubeCandidatesResponse,
} from 'fym-shared/src/music24/musicVideoApi';
import { fymAPI, RELEASE_KIT_LIST_ID } from './fymAPI';
import type { SerializedKitAsset } from './fymAPI.types';

/** The song of a route under `/accounts/:accountId/release-kits/:catalogItemUuid`. */
export interface AddedSongOptions {
  accountId: number;
  catalogItemUuid: string;
}

export type StartAddedUploadOptions = AddedSongOptions & AddedUploadStartRequest;

export type CompleteAddedUploadOptions = AddedSongOptions & CompleteMultipartUploadRequest & { uploadId: string };

export type AbortAddedUploadOptions = AddedSongOptions & { uploadId: string; key: string };

export type GetYouTubeCandidatesOptions = AddedSongOptions & { themeId: AddedAssetTheme };

export type AddYouTubeAssetOptions = AddedSongOptions & AddYouTubeRequest;

export type DeleteAddedAssetOptions = AddedSongOptions & { assetUuid: string };

export type KitAssetStreamOptions = AddedSongOptions & { assetUuid: string };

export type AddedKitAssetResponse = AddedAssetResponse<SerializedKitAsset>;

function songUrl({ accountId, catalogItemUuid }: AddedSongOptions): string {
  return `accounts/${accountId}/release-kits/${encodeURIComponent(catalogItemUuid)}`;
}

/** An add or a remove changes the kit of the song, the music video field of the catalog row and the publish records. */
function songTags(catalogItemUuid: string) {
  return [
    { type: 'ReleaseKit' as const, id: catalogItemUuid },
    { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
    { type: 'CatalogItem' as const, id: catalogItemUuid },
    'CatalogItem' as const,
    { type: 'AssetPublish' as const, id: 'LIST' },
    'Work' as const,
    'Releases' as const,
  ];
}

/**
 * "Add yours" (spec 2026-10-09 §3.3) and the stream link of a kit asset file (§4.7). The routes
 * live under `/accounts/:accountId/release-kits/:catalogItemUuid`. The file goes straight to S3 in
 * parts (D5), like the master and the Composer video: start, PUT the parts, complete (or abort).
 */
export const addedAssetsApi = fymAPI.injectEndpoints({
  endpoints: (builder) => ({
    startAddedUpload: builder.mutation<PresignedMultipartUploadResponse, StartAddedUploadOptions>({
      query: ({ themeId, fileName, contentType, sizeBytes, ...song }) => ({
        url: `${songUrl(song)}/added/uploads`,
        method: 'POST',
        body: JSON.stringify({ themeId, fileName, contentType, sizeBytes }),
        headers: { 'content-type': 'application/json' },
      }),
    }),
    completeAddedUpload: builder.mutation<AddedKitAssetResponse, CompleteAddedUploadOptions>({
      query: ({ uploadId, key, parts, ...song }) => ({
        url: `${songUrl(song)}/added/uploads/${encodeURIComponent(uploadId)}/complete`,
        method: 'POST',
        body: JSON.stringify({ key, parts }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => songTags(catalogItemUuid),
    }),
    abortAddedUpload: builder.mutation<void, AbortAddedUploadOptions>({
      query: ({ uploadId, key, ...song }) => ({
        url: `${songUrl(song)}/added/uploads/${encodeURIComponent(uploadId)}?${qs.stringify({ key })}`,
        method: 'DELETE',
      }),
    }),
    // The videos of the connected YouTube channel (the synced posts, no live call). The song tag
    // reads it again after an add, so "Linked to another song" stays right.
    getYouTubeCandidates: builder.query<YouTubeCandidatesResponse, GetYouTubeCandidatesOptions>({
      query: ({ themeId, ...song }) => ({
        url: `${songUrl(song)}/added/youtube-candidates?${qs.stringify({ themeId })}`,
        method: 'GET',
      }),
      providesTags: (result, error, { catalogItemUuid }) => [
        { type: 'ReleaseKit' as const, id: catalogItemUuid },
        'PromoChannels' as const,
      ],
    }),
    addYouTubeAsset: builder.mutation<AddedKitAssetResponse, AddYouTubeAssetOptions>({
      query: ({ themeId, url, videoId, ...song }) => ({
        url: `${songUrl(song)}/added/youtube`,
        method: 'POST',
        body: JSON.stringify({ themeId, url, videoId }),
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => songTags(catalogItemUuid),
    }),
    deleteAddedAsset: builder.mutation<void, DeleteAddedAssetOptions>({
      query: ({ assetUuid, ...song }) => ({
        url: `${songUrl(song)}/added/${encodeURIComponent(assetUuid)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { catalogItemUuid }) => songTags(catalogItemUuid),
    }),
    // A 1-hour presigned GET of the file of a kit asset version, to play it (§4.7). POST, so a
    // read-only token cannot make a link.
    createKitAssetStreamLink: builder.mutation<KitAssetStreamResponse, KitAssetStreamOptions>({
      query: ({ assetUuid, ...song }) => ({
        url: `${songUrl(song)}/assets/${encodeURIComponent(assetUuid)}/stream`,
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'content-type': 'application/json' },
      }),
    }),
  }),
});

export const {
  useStartAddedUploadMutation,
  useCompleteAddedUploadMutation,
  useAbortAddedUploadMutation,
  useGetYouTubeCandidatesQuery,
  useAddYouTubeAssetMutation,
  useDeleteAddedAssetMutation,
  useCreateKitAssetStreamLinkMutation,
} = addedAssetsApi;
